// @ts-strict-ignore
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild, AfterViewInit
} from '@angular/core';
import { SohoBusyIndicatorDirective, SohoDataGridComponent, SohoDataGridToggleRowEvent } from 'ids-enterprise-ng';
import {
  LMBusyIndicatorService,
  LMContextService,
  LMErrorUtilService,
  LMFormatUtilService,
  LMModalWrapperService,
  LMRouteComponentService,
  LMUrlUtilService
} from '@services/index';
import { AppConstants, ListRequestType, LMDialogButtonKey } from '@utils/app.constants';
import { LMFieldPickerService } from './services';
import { lmFieldPickerFormatter, lmFieldPickerHyperLinkFormatter } from './formatter/lm-fieldpicker.formatter';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import { LMGlobalPersonalizationService } from '@app/states';
import {
  LMFieldPickerIncludeOptions,
  LMFieldPickerOptionState,
  LMFieldPickerRequest,
  LMFieldPickerResponse,
  LMFieldPickerSearchComparator,
  LMFieldPickerSearchOptions,
  LMFieldPickerSearchScope,
  LMFieldPickerSelectOptions,
  LMOperatorInfo
} from '@modal/field-picker/lm-field-picker';
import { LMNotificationComponent } from '@components/notification';
import { LMFormRequestService } from '@form/services';
import { Subject } from 'rxjs';
import { LMListDatagridWrapperComponent } from '@components/list/lm-list-datagrid-wrapper.component';


@Component({
  selector: 'lm-field-picker',
  standalone: false,
  templateUrl: './lm-field-picker.component.html',
  styleUrls: [ './lm-field-picker.component.scss' ],
  providers: [ LMRouteComponentService ]
  })
export class LMFieldPickerComponent implements OnInit, AfterViewChecked, OnDestroy, AfterViewInit {

  @ViewChild('DatagridWrapper') datagridWrapper: LMListDatagridWrapperComponent;
  
  get dataGridComponent(): SohoDataGridComponent {
    return this.datagridWrapper?.sohoDataGridComponent;
  }

  @ViewChild(SohoBusyIndicatorDirective) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChild(LMNotificationComponent) lmNotificationPlaceholder: LMNotificationComponent;

  @Input() options: LMFieldPickerSelectOptions;
  @Input() lookupUrl: string;
  @Input() selectedFieldSpec: UIFieldSpec;
  @Input() operatorInfo: LMOperatorInfo;

  @Output() rowSelectedEvent = new EventEmitter<LMFieldPickerRowSelectionEvent>();
  @Output() uiFieldSpecSelectionEvent = new EventEmitter<LMFieldPickerRowSelectionEvent>();

  public static NAME_COLUMN_ID = 'fieldNameCol';
  public static SEARCH_FROM_HERE_COLUMN_ID = 'searchFrom';
  public static LABEL_COLUMN_ID = 'labelCol';
  public static TYPE_COLUMN_ID = 'fieldTypeCol';
  
  public fieldsFromDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-field-picker-fields-from' } ];
  public searchScopeDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-field-picker-search-scope' } ];
  public fieldTypeDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-field-picker-field-type' } ];

  EMPTY_DATA: SohoEmptyMessageOptions = {
    title: Soho.Locale.translate('FieldPickerEmptyDataMessage'),
    icon: 'icon-empty-no-data-new',
    color: 'graphite'
  };

  showingOperatorBuilder = false;
  fieldTypes: Array<LMFieldPickerOptionState>;
  fieldNameComparators = [
    { value: 'contains',    text: Soho.Locale.translate('Contains'),    icon: 'filter-contains' },
    { value: 'startsWith',  text: Soho.Locale.translate('StartsWith'),  icon: 'filter-start-with' },
    { value: 'endsWith',    text: Soho.Locale.translate('EndsWith'),    icon: 'filter-end-with' },
    { value: 'equals',      text: Soho.Locale.translate('Equals'),      icon: 'filter-equals' },
  ];

  searchScope: LMFieldPickerSearchScope = 'normal';
  includeOptions: LMFieldPickerIncludeOptions[] = [];
  relatedFieldFilter: string;
  relatedFieldFilterComparator: LMFieldPickerSearchComparator;
  searchOptions: LMFieldPickerSearchOptions = { nameValue: '', nameComparator: 'contains', };
  gridOptions: SohoDataGridOptions;
  emptyMessage: SohoEmptyMessageOptions = this.EMPTY_DATA;
  searchingFromHere = false;
  searchingFromHereTags: Array<LMListSearchTag> = [];
  searchingFromHereContext: Array<UIFieldSpec> = [];
  useCompactFormLayout = false;
  businessClasses: Array<string>;
  validPeriodsForMeasuresOnly = [
    { value: 'period', label: Soho.Locale.translate('FieldPicker_periodOption_label') },
    { value: 'week', label: Soho.Locale.translate('FieldPicker_weekOption_label') },
    { value: 'month', label: Soho.Locale.translate('FieldPicker_monthOption_label') },
    { value: 'quarter', label: Soho.Locale.translate('FieldPicker_quarterOption_label') },
    { value: 'year', label: Soho.Locale.translate('FieldPicker_yearOption_label') },
  ];

  businessClassSource = (response: any, searchTerm: any) => {
    response(this.businessClasses, true);
  };

  get selectedRow(): UIFieldSpec {
    return this._selectedRow;
  }

  _selectedRow: UIFieldSpec;

  get rowNumber(): number {
    return this._rowNumber;
  }

  _rowNumber = 0;

  get fieldOperatorInfo(): LMOperatorInfo {
    return this._fieldOperatorInfo;
  }

  _fieldOperatorInfo: LMOperatorInfo;

  private cancelNotifier$: Subject<boolean>;
  private dataLoaded = false;
  private loadGrid = true;
  private _localDataset: Array<UIFieldSpec> = [];

  constructor(protected lmBusyIndicatorService: LMBusyIndicatorService,
    private ngZone: NgZone,
    private changeDetectionRefService: ChangeDetectorRef,
    private lmFieldPickerService: LMFieldPickerService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmContextService: LMContextService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmFormRequestService: LMFormRequestService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmModalWrapperService: LMModalWrapperService,
  ) {
    this.useCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;
  }

  ngOnInit() {

    if (this.operatorInfo){
      this._selectedRow = this.selectedFieldSpec;
      this._fieldOperatorInfo = this.operatorInfo ? this.operatorInfo : this.lmFieldPickerService.blankOperatorInfo();
      this.showingOperatorBuilder = true;
      this.loadGrid = false;
    }

    this.searchScope = this.options.view === 'RelatedLinks' ? 'max' : 'normal';
    this.loadBusClasses(this.options.businessClass);
    this.buildSearchFieldTypeOptions();
    this.buildGrid();
  }

  ngAfterViewInit() {
    if (this.loadGrid) {
      this.loadInitialPage();
    }
  }

  ngAfterViewChecked() {
    if (this.dataLoaded) {
      this.dataLoaded = false;
      // LMCLIENT-38564
      setTimeout(() => this.handleInitialFocus());
    }
  }

  ngOnDestroy() {
    this.cleanUpCancelNotifier();
  }

  markForCheck() {
    this.changeDetectionRefService.detectChanges();
  }

  showBusyIndicator() {
    this.lmBusyIndicatorService.showBusyIndicator(this.busyIndicator);
  }

  hideBusyIndicator() {
    this.lmBusyIndicatorService.hideBusyIndicator(this.busyIndicator);
  }

  private loadBusClasses(businessClass: string) {
    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    let url = `${baseUrl}/config/All`;
    url = this.lmUrlUtilService.propagateSessionKeys(url, '?');

    this.lmFormRequestService.getRelatedBusClasses(url, businessClass)
      .subscribe((busClasses: any) => {
        this.businessClasses = [];
        busClasses.forEach((busClass) => this.businessClasses.push(busClass.name));
      },
      (error: any) => {
        // notify listeners
      });
  }

  private buildSearchFieldTypeOptions() {
    this.fieldTypes = [
      { value: 'onlyTextFields',          label: Soho.Locale.translate('FieldPicker_text_type_label') },
      { value: 'onlyBooleanFields',       label: Soho.Locale.translate('FieldPicker_boolean_type_label') },
      { value: 'onlyNumberFields',        label: Soho.Locale.translate('FieldPicker_number_type_label') },
      { value: 'onlyDateFields',          label: Soho.Locale.translate('FieldPicker_date_type_label') },
      { value: 'onlyBinaryObjectFields',  label: Soho.Locale.translate('FieldPicker_binary_type_label') },
      { value: 'onlyKeys',                label: Soho.Locale.translate('FieldPicker_keyfield_type_label') },
      { value: 'onlyUserFields',          label: Soho.Locale.translate('FieldPicker_userfield_type_label') },
    ];

    if (this.options.includeAuditFields) {
      this.fieldTypes.push({ value: 'onlyAuditFields', label: Soho.Locale.translate('FieldPicker_auditfield_type_label') });
    }
  }

  private buildGrid() {
    const headerColumns: ListGridColumn[] = [
      {
        id:             LMFieldPickerComponent.SEARCH_FROM_HERE_COLUMN_ID,
        name:           '',
        displayField:   { columnId: 'fieldName', type: AppConstants.TEXT },
        icon:           'search-folder',
        sortable:       false,
        resizable:      false,
        minWidth:       50,
        maxWidth:       50,
        formatter:      this.LMLinkFormatter,
        align:          'center',
        singleline:     true,
        click:          (event: MouseEvent, args: SohoDataGridColumnClickData[] ) => this.onColumnClicked(event, args, LMFieldPickerComponent.SEARCH_FROM_HERE_COLUMN_ID),
        tooltip:        this.LMListCellTooltip
      },
      {
        id:             LMFieldPickerComponent.NAME_COLUMN_ID,
        name:           Soho.Locale.translate('FieldPicker_FieldNameLabel'),
        field:          'fieldName',
        displayField:   { columnId: 'fieldName', isTreeColumn: true, type: AppConstants.TEXT },
        sortable:       false,
        minWidth:       200,
        maxWidth:       300,
        resizable:      true,
        formatter:      this.LMFieldFormatter,
        singleline:     true,
      },
      {
        id:             LMFieldPickerComponent.LABEL_COLUMN_ID,
        name:           Soho.Locale.translate('FieldPicker_FieldLabelLabel'),
        field:          'label',
        displayField:   { columnId: 'label', type: AppConstants.TEXT },
        sortable:       false,
        resizable:      true,
        minWidth:       300,
        maxWidth:       300,
        formatter:      this.LMLinkFormatter,
        click:          (event: MouseEvent, args: SohoDataGridColumnClickData[] ) => this.onColumnClicked(event, args, LMFieldPickerComponent.LABEL_COLUMN_ID), //this.LMColumnClick,
        singleline:     true,
      },
      {
        id:             LMFieldPickerComponent.TYPE_COLUMN_ID,
        name:           Soho.Locale.translate('FieldPicker_FieldTypeLabel'),
        field:          'fieldType',
        displayField:   { columnId: 'fieldType', type: AppConstants.TEXT },
        sortable:       false,
        resizable:      true,
        minWidth:       125,
        formatter:      this.LMFieldFormatter,
        singleline:     true,
        tooltip:        this.LMListCellTooltip,
      },
    ];

    this.gridOptions = <SohoDataGridOptions>{
      columns:            headerColumns,
      selectable:         'single',
      editable:           false,
      paging:             false,
      treeGrid:           true,
      showDirty:          false,
      emptyMessage:       this.emptyMessage,
      rowHeight:          'short',
      clickToSelect:      true,
      stretchColumn:      LMFieldPickerComponent.NAME_COLUMN_ID,
      enableTooltips:     true,
      stickyHeader:       true,
      filterWhenTyping:   false,
    };

    // Now using 'light datagrid header color
    this.gridOptions['headerBackgroundColor'] = 'light';
  }

  private getUIFieldSpecData(row: number): UIFieldSpec {

    const flatDataViewArray = [];

    // Push root or multi roots
    this._localDataset.forEach((d) => {
      flatDataViewArray.push(d);
      if (d.children) {
        flatten(d.children);
      }
    });

    function flatten(data?: Array<UIFieldSpec>) {
      data.forEach((d) => {
        flatDataViewArray.push(d);

        if (d.children && d.children.length > 0) {
          flatten(d.children);
        }
      });
    }

    return flatDataViewArray[row];
  }

  private resetSearchTagCriteria() {
    this.searchingFromHereContext = [];
    this.searchingFromHereTags = [];
    this.searchingFromHere = false;
  }

  private refreshPage() {
    if (this.searchingFromHere) {
      const uiFieldSpec = this.searchingFromHereContext[this.searchingFromHereContext.length - 1];
      this.loadSearchFromHereData(uiFieldSpec);
    } else {
      this.loadInitialPage();
    }
  }

  private loadInitialPage() {
    this.cleanUpCancelNotifier();
    this.dataGridComponent?.unSelectAllRows();
    this._selectedRow = undefined;
    const params = this.buildRequestParams(this.options.businessClass);
    this.showBusyIndicator();
    this.closeNotification();

    this.lmFieldPickerService.getFieldSpecData(params, this.lookupUrl, this.options.businessClass).subscribe((response: LMFieldPickerResponse) => {

      if (this.handleServerError(response as LMError)) {
        this.hideBusyIndicator();
        return;
      }

      const data = response.data;
      this.loadDataInGrid(data);

      if (data?.length > 0) {
        this.dataLoaded = true;
        if (this.selectedFieldSpec) {
          this._rowNumber = data.findIndex((fieldSpec: UIFieldSpec, indx: number) => fieldSpec.fullLPLName === this.selectedFieldSpec.fullLPLName);
          this._rowNumber = this._rowNumber === -1 ? 0 : this._rowNumber;  
          this._selectedRow = data[this._rowNumber];
        } else {
        this._selectedRow = data[0];
        }
      }

      this.hideBusyIndicator();
    },
    error => {
      if (this.handleServerError(error.error as LMError)) {
        this.hideBusyIndicator();
        return;
      }
    });
  }

  private handleInitialFocus() {
    this.dataGridComponent?.scrollRowIntoView(this.rowNumber);
    this.dataGridComponent?.selectRows(this.rowNumber);
    this.rowSelectedEvent.emit({ row: this.selectedRow });
  }

  private loadChildren(parent: UIFieldSpec) {
    this.cleanUpCancelNotifier();
    const businessClass: string = this.options.businessClass;
    const params = this.buildRequestParams(businessClass, parent);

    if (params.searchOptions) {
      // DO NOT apply filter to children!!!!!
      params.searchOptions = undefined;
    }

    if (params.relatedFieldFilter) {
      // DO NOT apply related filet to children, want to explore and not be limited!!!!!
      params.relatedFieldFilter = undefined;
    }

    this.lmFieldPickerService.getFieldSpecData(params, this.lookupUrl, this.options.businessClass).subscribe((response: LMFieldPickerResponse) => {

      if (this.handleServerError(response as LMError)) {
        this.hideBusyIndicator();
        return;
      }

      const data = response.data;
      this.loadDataInGrid(data, parent);
      this.hideBusyIndicator();
    }, error => {
      if (this.handleServerError(error.error as LMError)) {
        this.hideBusyIndicator();
        return;
      }
    });
  }

  private loadSearchFromHereData(uiFieldSpec: UIFieldSpec) {
    this.cleanUpCancelNotifier();
    this.dataGridComponent?.unSelectAllRows();
    this._selectedRow = undefined;
    const params = this.buildRequestParams(this.options.businessClass, uiFieldSpec);
    this.showBusyIndicator();
    this.closeNotification();

    this.lmFieldPickerService.getFieldSpecData(params, this.lookupUrl, this.options.businessClass).subscribe((response: LMFieldPickerResponse) => {

      if (this.handleServerError(response as LMError)) {
        this.hideBusyIndicator();
        return;
      }

      const data = response.data;
      this.loadDataInGrid(data);

      if (data?.length > 0) {
        this.dataLoaded = true;
        this._selectedRow = data[0];
      }

      this.hideBusyIndicator();
    }, error => {
      if (this.handleServerError(error.error as LMError)) {
        this.hideBusyIndicator();
        return;
      }
    });
  }

  private buildSearchTag(uiFieldSpec: UIFieldSpec): LMListSearchTag {
    const parent: string = this.lmFieldPickerService.getUIFieldSpecFullName(uiFieldSpec);
    return {
      id: parent,
      label: Soho.Locale.translate('SearchingFromContextLabel'),
      tag: parent,
    };
  }

  private loadDataInGrid(data: Array<UIFieldSpec>, parent?: UIFieldSpec) {
    const depth = parent ? parent.depth + 1 : 1;

    data.forEach((uiFieldSpec) => {
      uiFieldSpec.depth = depth;
      if (uiFieldSpec.hasChildren) {
        uiFieldSpec.children = [];
        uiFieldSpec.isLeaf = false;
      } else {
        uiFieldSpec.isLeaf = true;
      }
    });

    if (parent) {
      parent.children = data;
      (parent as any).childrenLoaded = true;
    } else {
      this._localDataset = data;
    }

    data.map(function callback(uiFieldSpec: UIFieldSpec) {
      if (Array.isArray(uiFieldSpec.children)) {
        uiFieldSpec.children.map(callback);
      }
    });

    this.dataGridComponent.dataset = [].concat(this._localDataset);
  }

  private buildRequestParams(businessClass: string, parent?: UIFieldSpec): LMFieldPickerRequest {
    const searchOptions = this.hasSearchCriteria() ? this.searchOptions : undefined;

    return  {
      requestType: ListRequestType.FieldSpecForBusClass,
      businessClass,
      parent,
      view: this.options.view,
      searchOptions,
      includeOptions: this.includeOptions,
      includeAuditFields: this.options.includeAuditFields,
      searchScope: this.searchScope,
      relatedFieldFilter: this.relatedFieldFilter,
      relatedFieldFilterComparator: 'equals',
      mode: this.options.mode
    };
  }

  private runSearch() {
    if (!this.hasSearchCriteria()) {
      this.refreshPage();
      return;
    }

    this.cleanUpCancelNotifier();
    this.datagridWrapper.sohoDataGridComponent.unSelectAllRows();
    this._selectedRow = undefined;

    let businessClass: string;
    let parentUiFieldSpec;

    if (this.searchingFromHere) {
      const index = this.searchingFromHereContext.length - 1;
      parentUiFieldSpec = this.searchingFromHereContext[index];
      businessClass = parentUiFieldSpec.businessClass;

    } else {
      businessClass = this.options.businessClass;
    }

    this.dataGridComponent?.unSelectAllRows();
    this._selectedRow = undefined;
    const params = this.buildRequestParams(businessClass, parentUiFieldSpec);
    this.showBusyIndicator();
    this.closeNotification();

    this.lmFieldPickerService.getCancellableFieldSpecData(params, this.lookupUrl, this.options.businessClass).subscribe((response: LMFieldPickerResponse) => {

      if (response.status === 'STARTED') {
        this.cancelNotifier$ = response.cancelableNotifier;
        return;
      } else if (response.status === 'WAITING') {
        this.showNotification('Search is taking a while.', 'Click here to cancel your search');
        return;
      } else if (response.status === 'CANCELED') {
        this.hideBusyIndicator();
        this.closeNotification();
        this.cancelNotifier$ = null;
        return;
      } else {
        this.cancelNotifier$ = null;
        this.closeNotification();
      }

      if (this.handleServerError(response as LMError)) {
        this.hideBusyIndicator();
        return;
      }

      if (response.message) {
        this.showNotification(response.message);
      }

      const data = response.data;

      this.loadDataInGrid(data);

      if (data?.length > 0) {
        this.dataLoaded = true;
        this._selectedRow = data[0];
      }

      this.hideBusyIndicator();
    });
  }

  private resetSearchCriteria() {
    this.searchOptions.nameValue = '';
    this.includeOptions = [];
    this.searchScope = this.options.view === 'RelatedLinks' ? 'max' : 'normal';
  }

  private hasSearchCriteria(): boolean {
    const searchValue = this.searchOptions.nameValue;
    return !(!searchValue || searchValue.trim().length === 0);
  }

  private _toggleOperator() {
    if (this.showingOperatorBuilder) {
      this.hideOperatorBuilder();
    } else {
      this.showOperatorBuilder();
    }
  }

  private showOperatorBuilder() {
    this._fieldOperatorInfo = this.lmFieldPickerService.blankOperatorInfo();
    this.showingOperatorBuilder = true;
  }

  private hideOperatorBuilder() {
    this._fieldOperatorInfo = undefined;
    this.showingOperatorBuilder = false;

    if (!this.loadGrid) {
      this.loadGrid = true;
      this.loadInitialPage();
    }
  }

  private showNotification(message: string, actionMessage?: string) {
    this.ngZone.run(() => {
      this.lmNotificationPlaceholder.isVisible = true;
      this.lmNotificationPlaceholder.message = message;
      this.lmNotificationPlaceholder.actionMessage = actionMessage;
    });
  }

  private closeNotification() {
    if (this.lmNotificationPlaceholder.isVisible) {
      this.ngZone.run(() => {
        this.lmNotificationPlaceholder.isVisible = false;
        this.lmNotificationPlaceholder.message = undefined;
        this.lmNotificationPlaceholder.actionMessage = undefined;
      });
    }
  }

  private handleServerError(error: LMError): boolean {
    if (error?.status === AppConstants.STATUS_SYSTEM_ERROR || error?.statusText ===  AppConstants.STATUS_SYSTEM_ERROR) {
      this.lmErrorUtilService.handleViewException(error);
      return true;
    }
    return false;
  }

  onSearchNameEnter(event) {
    this.runSearch();
  }

  onSearchClicked(event) {
    this.runSearch();
  }

  onClearSearchClicked(event) {
    this.resetSearchCriteria();
    this.resetSearchTagCriteria();
    this.loadBusClasses(this.options.businessClass);
    this.relatedFieldFilter = '';
    this.refreshPage();
  }

  onFieldNameComparatorChange(event: SohoFieldFilteredEvent) {
    if (event?.filterOption?.value) {
      this.searchOptions.nameComparator = event.filterOption?.value as LMFieldPickerSearchComparator;
    }
  }

  onBusinessClassComparatorChange(event: SohoFieldFilteredEvent) {
    if (event?.filterOption?.value) {
      this.relatedFieldFilterComparator = event.filterOption?.value as LMFieldPickerSearchComparator;
    }
  }

  onSearchTagRemoved(removedSearchTag: LMListSearchTag) {
    if (this.searchingFromHereContext.length > 0) {
      this.searchingFromHereContext.pop();
    }

    if (this.searchingFromHereContext.length > 0) {
      const index = this.searchingFromHereContext.length - 1;
      const uiFieldSpec = this.searchingFromHereContext[index];
      this.searchingFromHereTags = [ this.buildSearchTag(uiFieldSpec) ];
      this.loadBusClasses(uiFieldSpec.relatedBusinessClass);
      this.loadSearchFromHereData(uiFieldSpec);
    } else {
      this.resetSearchTagCriteria();
      this.loadBusClasses(this.options.businessClass);
      this.loadInitialPage();
    }
  }

  onRowSelected(event: SohoDataGridRowClicked) {
    if (!event) {
      return;
    }

    const row: number = event.row;
    this._selectedRow = this.getUIFieldSpecData(row) as UIFieldSpec;
    const lmFieldPickerRowSelectionEvent: LMFieldPickerRowSelectionEvent = <LMFieldPickerRowSelectionEvent>{ row: this.selectedRow };
    this.rowSelectedEvent.emit(lmFieldPickerRowSelectionEvent);
  }

  onRowDoubleClicked(event: SohoDataGridRowClicked) {
    if (!event) {
      return;
    }

    const row: number = event.row;
    this._selectedRow = this.getUIFieldSpecData(row) as UIFieldSpec;

    if (this.options.view === 'SimpleConditions'
    && (this.selectedRow.fieldType !== 'STATE' && this.selectedRow.fieldType !== 'BOOLEAN' && this.selectedRow.fieldType !== 'CONDITION')) {
      return;
    }

    if (this.selectedRow.fieldName === 'agent' || this.selectedRow.fieldName === 'context' || this.selectedRow.fieldName.endsWith(')')) {
      return;
    }

    const lmFieldPickerRowSelectionEvent: LMFieldPickerRowSelectionEvent = <LMFieldPickerRowSelectionEvent>{ row: this.selectedRow };
    this.uiFieldSpecSelectionEvent.emit(lmFieldPickerRowSelectionEvent);
  }

  onExpandRow(event: SohoDataGridToggleRowEvent) {
    const rowData: UIFieldSpec = event.rowData;
    if ((rowData as any).childrenLoaded) {
      return;
    }

    this.showBusyIndicator();
    this.closeNotification();

    setTimeout(() => this.loadChildren(rowData));
  }

  onCollapseRow(event: SohoDataGridToggleRowEvent) {
    // no -op
  }

  onFieldSpecSelected(callback) {
    if (this.selectedRow === undefined || (this.selectedRow.fieldName === 'agent' || this.selectedRow.fieldName === 'context' || this.selectedRow.fieldName.endsWith(')'))) {
      return;
    }
    if (this.options.view === 'SimpleConditions'
      && (this.selectedRow.fieldType !== 'BOOLEAN' && this.selectedRow.fieldType !== 'STATE' && this.selectedRow.fieldType !== 'CONDITION')) {
      const dialogId = 'ConditionSelectionMessage';
      const messageDialog = this.lmModalWrapperService
        .message()
        .message(Soho.Locale.translate('ListPersonalizationConditionErrorMessage'))
        .title(Soho.Locale.translate('ListPersonalizationConditionSelectionTitle'))
        .buttons([
          {
            id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CLOSE ]),
            text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
            click: (e, modal) => {
              modal.close();
            }
          }
        ])
        .open();
      return;
    }

    callback();
  }

  onNotificationEvent(event: LMNotificationEvent) {
    this.cancelSearchRequest();
  }

  private onColumnClicked(event: Event, args: SohoDataGridColumnClickData[], columnId: string) {
    if (args?.length > 0) {
      // const cell = args[0].cell;
      const uiFieldSpec = args[0].item;
      // const column = this.dataGridComponent?.getColumns()[cell as any]; // SOHO type seems wrong,  should be a number not an HTMLElement
      // const columnId = column?.id;

      if (LMFieldPickerComponent.SEARCH_FROM_HERE_COLUMN_ID === columnId) {
        this.resetSearchCriteria();
        this.searchingFromHereContext.push(uiFieldSpec);
        this.searchingFromHereTags = [ this.buildSearchTag(uiFieldSpec) ];
        this.searchingFromHere = true;
        this.loadBusClasses(uiFieldSpec.relatedBusinessClass);
        this.loadSearchFromHereData(uiFieldSpec);
      }

      if (LMFieldPickerComponent.LABEL_COLUMN_ID === columnId) {
        this.lmFieldPickerService.openFieldHelpDialog(uiFieldSpec);
      }
    }
  }

  private onCellTooltip(row: number, cell: number, value: any, columnDef: ListGridColumn, uiFieldSpec: UIFieldSpec, dataGrid: SohoDataGridStatic): string {
    if (LMFieldPickerComponent.SEARCH_FROM_HERE_COLUMN_ID === columnDef.id) {
      return this.lmFormatUtilService.formatMessage(Soho.Locale.translate('SearchFromContextLabel'), uiFieldSpec.fieldName);
    }

    if (LMFieldPickerComponent.TYPE_COLUMN_ID === columnDef.id && uiFieldSpec.lmrkFieldType)  {
      return uiFieldSpec.lmrkFieldType;
    }
  }

  private decorateAsKey(uiFieldSpec): boolean {
    if (uiFieldSpec?.isOntologyContext) {
      if (this.searchingFromHereContext.length > 0) {
        const searchFromUiFieldSpec = this.searchingFromHereContext[this.searchingFromHereContext.length -1];
        return uiFieldSpec?.businessClass === searchFromUiFieldSpec.relatedBusinessClass;
      } else {
        return uiFieldSpec?.businessClass === this.options.businessClass;
      }
    }
    return false;
  }

  private cleanUpCancelNotifier(): void {
    if (this.cancelNotifier$) {
      this.cancelNotifier$.next(true);
      this.cancelNotifier$ = null;
    }
  }

  private cancelSearchRequest() {
    if (this.cancelNotifier$) {
      this.cancelNotifier$.next(true);
    }
  }

  LMFieldFormatter = (row: number, cell: number, value: any, headerColumns: ListGridColumn, uiFieldSpec: UIFieldSpec, dataGrid: SohoDataGridStatic): string =>
    lmFieldPickerFormatter(this.lmFieldPickerService, this.lmFormatUtilService, this.searchingFromHereContext, this.searchOptions.nameValue, this.decorateAsKey(uiFieldSpec), row, cell, value, headerColumns, uiFieldSpec, dataGrid);

  LMLinkFormatter = (row: number, cell: number, value: any, headerColumns: ListGridColumn, uiFieldSpec: UIFieldSpec, dataGrid: SohoDataGridStatic): string =>
    lmFieldPickerHyperLinkFormatter(this.lmContextService, row, cell, value, headerColumns, uiFieldSpec, dataGrid);

  // LMColumnClick = (e: Event, args: SohoDataGridColumnClickData[]) => this.onColumnClicked(e, args);

  LMListCellTooltip = (row: number, cell: number, value: any, columnDef: ListGridColumn, uiFieldSpec: UIFieldSpec, dataGrid: SohoDataGridStatic): string =>
    this.onCellTooltip(row, cell, value, columnDef, uiFieldSpec, dataGrid);

  public toggleOperator() {
    this.ngZone.run(() => this._toggleOperator());
  }
}

